import { FixtureGameHistoryProvider, FixtureReplayRoundProvider, FixtureRoundProvider, loadLocalizationDictionaryFromBundle, type GameDefinition } from "@slot/game-shell";
import { version } from "../package.json";
import { BOOT_FALLBACK_GRID } from "./composition/bootFallbackGrid";
import { createPresentation } from "./composition/createPresentation";
import { installMergeCandyHook } from "./dev/installMergeCandyHook";
import { createFeaturePurchaseOffers } from "./feature/createFeaturePurchaseOffers";
import { SECRET_BONUS_OFFER_ID } from "./features/secret-bonus/secretBonusOffer";
import { GRID_SIZE } from "./gridSize";
import { INTRO_SPLASH_SPEC } from "./info/IntroSplashSpec";
import { gameEnDictionary } from "./localization/game.en";
import { createScene, type MergeCandyScene } from "./scene/createScene";
import { BAR_LAYOUT } from "./scene/GameScene";
import { SELECTABLE_FIXTURES } from "./sim/fixtureRegistry";
import { BACKGROUND_TUNING } from "./tuning/backgroundTuning";
import { createFixtureCatalog } from "./sim/fixtures";
import { mergeChain } from "./sim/fixtures/base/merge_chain";
import { fsMergeTrigger } from "./sim/fixtures/feature/fs_merge_trigger";
import { HISTORY_FIXTURE } from "./sim/historyFixture";
import { BIG_WIN_TIERS } from "./sim/offlineMath";

/**
 * mergecandy as one object (plan §2.2): what the shell's boot, chrome and offline data take from this title, and the
 * factories for its scene and its round presentation.
 */
export const mergeCandy: GameDefinition<MergeCandyScene> = {
  id: "mergecandy",
  // The package's own version (games/mergecandy/package.json), so a release bumps one number.
  version,
  grid: GRID_SIZE,
  bootFallbackGrid: BOOT_FALLBACK_GRID,

  boot: {
    locale: {
      baseDictionary: gameEnDictionary,
      loadRemoteDictionary: ({ locale, gameBundleRegistry }) => loadLocalizationDictionaryFromBundle(gameBundleRegistry, `localization/game.${locale}`),
    },
    // The offline boot's operator settings: the title's name, the purchase's price and the big-win tiers (all the server's online).
    fixtureSettingsOverrides: {
      gameTitle: "Merge Candy",
      // Placeholders (the math is not settled): the Bonus Buy at 100× the bet, the Secret Bonus's longer session at 150×.
      featurePriceMultipliersById: { free_spins_buy: 100, [SECRET_BONUS_OFFER_ID]: 150 },
      bigWinTiers: BIG_WIN_TIERS.map((tier) => ({ ...tier })),
    },
    // The shell loads its core set (`shared-core`, `game-shared-visuals`, `shell-ui`) itself; the title has no bundle of its own.
    extraBundles: [],
    // No sounds yet: the shell's mapping plays its own bank's ids, and the title registers none.
    audioOverrides: null,
  },

  // The candy factory (decision 69): its paintings as the boot bundle's stills, the phone's and the wide one, which the
  // loader, the intro and the scene show by the screen's orientation; under a transparent canvas ("dom"), where the scene
  // animates them over the still (`GameBackground`), drawn straight to the screen.
  background: {
    stills: { portrait: "background-portrait", landscape: "background-landscape" },
    draw: "dom",
    overscan: BACKGROUND_TUNING.overscan,
  },

  chrome: {
    introSplash: INTRO_SPLASH_SPEC,
    // The rules page states no number yet (the math is not settled), so the title adds no tokens of its own.
    buildExtraInfoTokens: () => ({}),
    featurePurchaseOffers: createFeaturePurchaseOffers(),
    // The control bar's candy art (decision 49; `tools/export_art.py` cuts it from the delivery): the gold spin button,
    // bet steps and Buy Bonus, the dark round buttons. The arrows, the stop face and the Buy Bonus disc are drawn in
    // colour, so the shell's tints (the arrows' feature-mode orange, the stop face's red) give way to the art; the bar's
    // feature HUD still says when free spins play.
    controlArt: { bundle: "game-shared-visuals", atlas: "sprites/controlArt", ownColours: ["spinIcon", "slamIcon", "buyBonusBtn"] },
    // The delivery's footer gradient: black, to 70 % at the screen's bottom edge.
    theme: { footerShade: "rgba(0, 0, 0, 0.7)" },
    // On a wide phone the controls go down and the grid grows into the empty sides (decision 78): `BAR_LAYOUT`.
    barLayout: BAR_LAYOUT,
  },

  fixtures: {
    createCatalog: () => createFixtureCatalog(),
    // Offline dev and the e2e suite: the fixture round robin, or a playlist of the selectable fixtures (the dev menu, the hook).
    createRoundProvider: () =>
      new FixtureRoundProvider(createFixtureCatalog(), SELECTABLE_FIXTURES, {
        simulatedLatencyMs: 50,
        cycleThroughFixtures: true,
        startGroup: "base",
        rejectLowBalanceForTesting: false,
        rejectedBalanceAmount: "420",
        rejectedCurrencyCode: "EUR",
      }),
    createHistoryProvider: () => new FixtureGameHistoryProvider(HISTORY_FIXTURE, { simulatedLatencyMs: 300 }),
    // `?replay=` / `?pending=`: the free spins for the history's biggest win, the ladder chain for any other id, the shell's `fail` for the failure path.
    createReplayRoundProvider: () =>
      new FixtureReplayRoundProvider({ roundsById: { "5100000000003": fsMergeTrigger }, defaultRound: mergeChain }, { simulatedLatencyMs: 300 }),
  },

  createScene,
  createPresentation,
  dev: {
    // `getBoard()` beside the shell's members, for the e2e suite.
    installHook: installMergeCandyHook,
  },
};
